import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function AdminPriceLists() {
  const [lists,   setLists]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ title: '', description: '', sortOrder: 0 });
  const [pdfFile, setPdfFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/pricelists/admin')
      .then(r => setLists(r.data.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ title: '', description: '', sortOrder: 0 });
    setPdfFile(null);
    setModal(true);
  };

  const openEdit = (pl) => {
    setEditing(pl);
    setForm({ title: pl.title, description: pl.description || '', sortOrder: pl.sortOrder || 0 });
    setPdfFile(null);
    setModal(true);
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    if (!form.title) { toast.error('Title required.'); return; }
    if (!editing && !pdfFile) { toast.error('PDF file required.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('sortOrder', form.sortOrder);
      if (pdfFile) fd.append('pdf', pdfFile);

      if (editing) {
        const r = await api.put(`/pricelists/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setLists(ls => ls.map(l => l._id === editing._id ? r.data.data : l));
        toast.success('Price list updated!');
      } else {
        const r = await api.post('/pricelists', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setLists(ls => [r.data.data, ...ls]);
        toast.success('Price list uploaded!');
      }
      setModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this price list? The PDF will also be removed.')) return;
    try {
      await api.delete(`/pricelists/${id}`);
      setLists(ls => ls.filter(l => l._id !== id));
      toast.success('Deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  const toggleActive = async (pl) => {
    try {
      const fd = new FormData();
      fd.append('title', pl.title);
      fd.append('isActive', !pl.isActive);
      const r = await api.put(`/pricelists/${pl._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLists(ls => ls.map(l => l._id === pl._id ? r.data.data : l));
    } catch { toast.error('Failed.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Price Lists</h1>
          <p className="font-body text-sm text-secondary mt-1">Upload and manage downloadable PDF price lists.</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <span className="material-symbols-outlined">upload_file</span> Upload PDF
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-36 skeleton" />)}
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-[64px] text-outline-variant">picture_as_pdf</span>
          <p className="font-headline font-bold text-xl uppercase mt-3 text-secondary">No price lists yet</p>
          <p className="font-body text-sm text-secondary mt-1 mb-6">Upload a PDF to get started.</p>
          <button onClick={openAdd} className="btn-primary">Upload First PDF</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {lists.map(pl => (
            <div key={pl._id} className={`bg-white border ${pl.isActive ? 'border-outline-variant/20' : 'border-outline-variant/10 opacity-60'} flex flex-col`}>
              {/* PDF icon header */}
              <div className="bg-surface-container-low px-5 py-5 flex items-center gap-4 border-b border-outline-variant/20">
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-[28px] text-red-600">picture_as_pdf</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-headline font-bold text-base uppercase tracking-tight text-on-surface leading-tight truncate">{pl.title}</p>
                  <p className="font-label text-xs text-secondary truncate mt-0.5">{pl.filename}</p>
                </div>
              </div>

              <div className="px-5 py-4 flex-1 flex flex-col gap-3">
                {pl.description && <p className="font-body text-sm text-secondary leading-relaxed">{pl.description}</p>}

                <div className="flex items-center justify-between mt-auto pt-2 flex-wrap gap-2">
                  <span className="font-label text-xs text-secondary">
                    {new Date(pl.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className={`badge ${pl.isActive ? 'badge-green' : 'badge-red'}`}>{pl.isActive ? 'Active' : 'Hidden'}</span>
                </div>

                <div className="flex gap-2 pt-2 border-t border-surface-container flex-wrap">
                  <a href={pl.url} target="_blank" rel="noreferrer" download
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-outline-variant font-label text-xs font-bold text-secondary hover:border-primary-container hover:text-primary-container transition-colors">
                    <span className="material-symbols-outlined text-[14px]">download</span> Preview
                  </a>
                  <button onClick={() => openEdit(pl)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-outline-variant font-label text-xs font-bold text-secondary hover:border-primary-container hover:text-primary-container transition-colors">
                    <span className="material-symbols-outlined text-[14px]">edit</span> Edit
                  </button>
                  <button onClick={() => toggleActive(pl)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-outline-variant font-label text-xs font-bold text-secondary hover:border-on-surface hover:text-on-surface transition-colors">
                    <span className="material-symbols-outlined text-[14px]">{pl.isActive ? 'visibility_off' : 'visibility'}</span>
                    {pl.isActive ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => del(pl._id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-outline-variant font-label text-xs font-bold text-secondary hover:border-error hover:text-error transition-colors">
                    <span className="material-symbols-outlined text-[14px]">delete</span> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Price List' : 'Upload Price List'}</h2>
              <button onClick={() => setModal(false)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="input-label">Title *</label>
                <input name="title" value={form.title} onChange={h} className="input-field" placeholder="e.g. Standard Fasteners Price List 2024" />
              </div>
              <div>
                <label className="input-label">Description</label>
                <textarea name="description" value={form.description} onChange={h} rows={3} className="input-field resize-none"
                  placeholder="Brief description of this price list..." />
              </div>
              <div>
                <label className="input-label">Sort Order</label>
                <input name="sortOrder" type="number" value={form.sortOrder} onChange={h} className="input-field" />
              </div>
              <div>
                <label className="input-label">PDF File {editing ? '(leave empty to keep existing)' : '*'}</label>
                <input ref={fileRef} type="file" accept="application/pdf"
                  onChange={e => setPdfFile(e.target.files[0])}
                  className="input-field" />
                {editing && (
                  <p className="font-label text-xs text-secondary mt-1">
                    Current: <span className="font-bold">{editing.filename}</span>
                  </p>
                )}
                {pdfFile && (
                  <p className="font-label text-xs text-primary-container mt-1">
                    New file: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary">
                {saving
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  : <><span className="material-symbols-outlined">save</span> {editing ? 'Update' : 'Upload'}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
